import {
  ButtonComponent,
  MYUI_FORM_CONTAINER,
  MyuiEditable,
  MyuiHintList,
  MyuiLabel,
  MyuiTemplate,
  type MyuiEditableType,
  type MyuiEvent,
  type MyuiStyleClassType,
  type MyuiStyleListType,
} from '@myre/ui-primitive-components';
import { DateFormatPipe } from '@myre/ui-primitive-core';

import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
  type ExistingProvider,
  type OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  type ValidationErrors,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs/operators';

import { type ISelectItem } from '../../questions/question-multi-base';

import { DatePickerComponent } from '../date-picker/date-picker.component';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { DropdownTemplate } from '../dropdown/dropdown.directive';

import {
  MyuiPeriodPickerKey,
  type MyuiPeriodPickerContext,
  type MyuiPeriodPickerOption,
  type MyuiPeriodPickerSignature,
  type MyuiPeriodPickerValue,
} from './period-picker.interface';
import { MyuiPeriodPickerValidator } from './period-picker.validator';

export const MYUI_PERIOD_PICKER_WORDING_PREFIX = '_library.periodPicker';

export const MYUI_PERIOD_PICKER_VALUE_ACCESSOR: ExistingProvider = {
  multi: true,
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MyuiPeriodPicker),
};

@Component({
  selector: 'myui-period-picker',
  viewProviders: [MYUI_FORM_CONTAINER],
  templateUrl: 'period-picker.component.html',
  providers: [MYUI_PERIOD_PICKER_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    DropdownComponent,
    DropdownTemplate,
    DatePickerComponent,
    FormsModule,
    MyuiHintList,
    MyuiLabel,
    MyuiTemplate,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    ReactiveFormsModule,
  ],
})
export class MyuiPeriodPicker
  extends MyuiEditable<MyuiPeriodPickerValue>
  implements OnInit
{
  protected _keys = MyuiPeriodPickerKey;
  protected _dateFormatter = inject(DateFormatPipe);

  private readonly _translateService = inject(TranslateService);

  public override mSelector = 'myui-period-picker';
  public override mContainer = inject(ControlContainer, { optional: true });

  public readonly closeOnApply = input(true);
  public readonly resetOnClose = input(true);
  public readonly emitOnChange = input(false);

  public readonly placeholder = input(this._wording('period'));

  public readonly types = input<ISelectItem<string>[]>([]);
  public readonly typesLabel = input(this._wording('chooseDateType'));

  public readonly options = input<MyuiPeriodPickerOption[]>([]);

  public readonly actionLabel = input(this._wording('apply'));
  public readonly endDateLabel = input(this._wording('endDate'));
  public readonly startDateLabel = input(this._wording('startDate'));

  public readonly panelStyle = input<MyuiStyleListType | undefined>();
  public readonly panelStyleClass = input<MyuiStyleClassType | undefined>();

  public readonly isFormValid = signal(false);
  public readonly isFormPristine = signal(true);
  public readonly formValue =
    signal<MyuiEditableType<MyuiPeriodPickerValue>>(null);

  public readonly opened = output<MyuiEvent<boolean>>();
  public readonly closed = output<MyuiEvent<boolean>>();
  public readonly applyed = output<MyuiEvent<boolean>>();
  public readonly selected = output<MyuiEvent<MyuiEditableType<Date>>>();

  public readonly typeSelected = output<MyuiEvent<ISelectItem<string>>>();

  public readonly labelTemplate = computed(() =>
    this._getTemplate<MyuiPeriodPickerContext>('label'),
  );

  public readonly defaultLabel = computed(() => {
    if (this.value()?.range) {
      const type = this.selectedType()?.label;

      const value = [type, this.shortDefaultLabel()]
        .filter(Boolean)
        .join(' — ')
        .trim();

      if (value) {
        return value;
      }
    }

    return this.placeholder();
  });

  public readonly shortDefaultLabel = computed(() => {
    if (this.value()?.range) {
      const to = this.formatDate(this.value()?.range?.to);
      const from = this.formatDate(this.value()?.range?.from);

      return [to, from].filter(Boolean).join(' - ');
    }

    return this.placeholder();
  });

  public readonly hasTypes = computed(
    () => this.types() && this.types().length > 0,
  );

  public readonly hasOptions = computed(
    () => this.options() && this.options().length > 0,
  );

  public readonly hasValue = computed(() => !!this.value());

  public readonly selectableOptions = computed<ISelectItem<string | null>[]>(
    () => {
      return this.options().map(option => {
        return {
          value: option.key,
          label: option.label,
        };
      });
    },
  );

  public readonly selectedOption = linkedSignal(() => {
    const value = this.value();

    if (!value || !(MyuiPeriodPickerKey.Option in value)) {
      return null;
    }

    const optionValue = value[MyuiPeriodPickerKey.Option];

    return this.options().find(o => o.key === optionValue) ?? null;
  });

  public readonly selectedType = linkedSignal(() => {
    return (
      this.types().find(
        t => t.value === this.value()?.[MyuiPeriodPickerKey.Type],
      ) ?? null
    );
  });

  protected readonly _context = computed<MyuiPeriodPickerContext>(() => {
    return {
      $implicit: this.value(),
      label: this.defaultLabel(),
    };
  });

  protected _optionPicker =
    viewChild<DropdownComponent<unknown>>('optionPicker');

  protected _typePicker = viewChild<DropdownComponent<unknown>>('typePicker');

  protected _form = computed(() => {
    return new FormGroup<MyuiPeriodPickerSignature>({
      [MyuiPeriodPickerKey.Type]: new FormControl<string>(
        null,
        MyuiPeriodPickerValidator.type(this.hasTypes()),
      ),
      [MyuiPeriodPickerKey.Option]: new FormControl<string>(null),
      [MyuiPeriodPickerKey.Range]: new FormGroup(
        {
          [MyuiPeriodPickerKey.To]: new FormControl<Date>(null),
          [MyuiPeriodPickerKey.From]: new FormControl<Date>(null),
        },
        [MyuiPeriodPickerValidator.range],
      ),
    });
  });

  protected _rangeErrors = signal<ValidationErrors | null>(null);

  protected _hasRangeErrors = computed(() => !!this._rangeErrors());

  protected readonly _rangeStart = computed(
    () => this.formValue()?.range?.from,
  );

  protected readonly _rangeEnd = computed(() => this.formValue()?.range?.to);

  constructor() {
    super();

    this.showErrors.set(true);

    effect(onCleanup => {
      const form = this._form();
      const subscription = form.statusChanges
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          tap(() => {
            this.isFormValid.set(form.valid);
            this.isFormPristine.set(form.pristine);
            this._rangeErrors.set(
              form.get(MyuiPeriodPickerKey.Range)?.errors ?? null,
            );
          }),
        )
        .subscribe();

      onCleanup(() => subscription.unsubscribe());
    });

    effect(onCleanup => {
      const form = this._form();
      const subscription = form.valueChanges
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          tap(() => {
            this.formValue.set(
              this._form().value as MyuiEditableType<MyuiPeriodPickerValue>,
            );

            if (this.emitOnChange()) {
              this._emitChanges();
            }
          }),
        )
        .subscribe();

      onCleanup(() => subscription.unsubscribe());
    });
  }

  public ngOnInit(): void {
    this._initializeFormValue();
  }

  // Actions
  public open(event?: Event): void {
    this._optionPicker()?.focus(event);
  }

  public close(event?: Event): void {
    this._optionPicker()?.blur(event);
  }

  public openTypes(): void {
    if (!this._optionPicker().isFocused()) {
      this.open();
    }

    this._typePicker()?.blur();
  }

  public closeTypes(): void {
    this._typePicker()?.blur();
  }

  // Events
  public onApply(event?: Event): void {
    this._emitChanges();
    this._notifyOnApplied(event);

    if (this.closeOnApply()) {
      this._optionPicker()?.blur();
    }
  }

  protected _onOptionChange(value: string | string[] | null): void {
    const key = Array.isArray(value) ? value[0] : value;
    const option = this.options().find(o => o.key === key) ?? null;

    this.selectedOption.set(option);

    if (option) {
      const range = option.compute(option, this.value());
      this._form().patchValue({ [MyuiPeriodPickerKey.Range]: range });
    }
  }

  protected _onOptionClose(event?: Event): void {
    if (this.resetOnClose() && !this.isFormPristine()) {
      this._resetFormValue();
      this._resetFormStatus();
    }

    this._notifyOnClose(event);
  }

  protected _onOptionOpen(event?: Event): void {
    this._notifyOnOpen(event);
  }

  protected _onRangeChange(_: Date | null): void {
    this._form().patchValue({ [MyuiPeriodPickerKey.Option]: null });
  }

  // Notifiers
  protected _notifyOnChanged(event?: Event): void {
    this.changed.emit({
      value: this.value(),
      originalEvent: event,
    });
  }

  protected _notifyOnApplied(event?: Event): void {
    this.applyed.emit({
      value: true,
      originalEvent: event,
    });
  }

  protected _notifyOnOpen(event?: Event): void {
    this.opened.emit({
      value: false,
      originalEvent: event,
    });
  }

  protected _notifyOnClose(event?: Event): void {
    this.closed.emit({
      value: false,
      originalEvent: event,
    });
  }

  // Emitters
  protected _emitChanges(): void {
    let value: MyuiEditableType<MyuiPeriodPickerValue> = this.value();

    if (this.formValue()) {
      if (value) {
        value = { ...value, ...this.formValue() };
      } else {
        value = this.formValue();
      }
    }

    this.setValue(value);
    this._notifyOnChanged();
  }

  // Value
  public override resetValue(): void {
    super.resetValue();
    this._resetFormValue();
    this._resetFormStatus();
  }

  public setValue(value: MyuiEditableType<MyuiPeriodPickerValue>): void {
    this.updateValue(value);
    this._onModelChange(this.value());
    this._onModelTouched();
  }

  public updateValue(value: MyuiEditableType<MyuiPeriodPickerValue>): void {
    this.value.set(value);
    this._resetFormValue();
  }

  protected _initializeFormValue(): void {
    let range = this.value()?.range;

    const value = this.value();

    if (
      !range &&
      value &&
      MyuiPeriodPickerKey.Option in value &&
      value[MyuiPeriodPickerKey.Option]
    ) {
      range = this.selectedOption().compute(
        this.selectedOption(),
        this.value(),
      );
      this.setValue({ ...this.value(), range });
    }

    this._form().patchValue(this.value());
  }

  protected _resetFormValue(): void {
    this._form().reset(this.value());
  }

  protected _resetFormStatus(): void {
    this._form().markAsPristine();
    this._form().markAsUntouched();
    this._form().setErrors(null);

    this.isFormValid.set(true);
    this.isFormPristine.set(true);
    this._rangeErrors.set(null);
  }

  // Format
  public formatDate(date: Date | null): string {
    if (!date) {
      return '';
    }

    return this._dateFormatter.transform(date, 'dd/MM/yyyy');
  }

  // Wording
  protected _error(key: string): string {
    return this._wording(`errors.${key}`);
  }

  protected _wording(
    key: string,
    prefix = MYUI_PERIOD_PICKER_WORDING_PREFIX,
  ): string {
    return (
      this._translateService.instant([prefix, key].filter(Boolean).join('.')) ??
      key
    );
  }
}
